<template>
  <div
    class="dropdown multi-select-dropdown"
    ref="dropdown"
  >
    <button
      ref="button"
      class="form-control dropdown-toggle dropdown-button"
      type="button"
      @click="toggleDropdown"
      :disabled="disabled"
      :aria-expanded="isOpen"
      :style="{ width: buttonWidth }"
    >
      {{ selectedLabels }}
    </button>
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menu"
        class="dropdown-menu show dropdown-menu-scrollable multi-select-dropdown__menu"
        :style="menuStyle"
      >
        <input
          type="text"
          v-model="searchQuery"
          class="form-control dropdown-search"
          :placeholder="t('common.searchPlaceholder')"
          :disabled="disabled"
        />
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="dropdown-item"
          :class="{ disabled: isOptionDisabled(option.value) }"
          @mousedown.prevent="toggleOption(option.value)"
        >
          <div class="form-check">
            <input
              :id="`${menuId}-${option.value}`"
              class="form-check-input"
              type="checkbox"
              tabindex="-1"
              :checked="isSelected(option.value)"
              :disabled="isOptionDisabled(option.value)"
            />
            <span
              class="form-check-label"
              :class="{ 'text-muted': isOptionDisabled(option.value) }"
            >
              {{ option.label }}
            </span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  clipPathBelowModuleHeader,
  cssRectFromVisual,
  cssViewportSize,
  viewportMenuPlacement,
} from '@/lib/overlayClip'
import { PAGE_ZOOM_EVENT } from '@/lib/pageZoom'

const props = defineProps({
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  modelValue: {
    type: Array,
    required: true,
    default: () => [],
  },
  buttonWidth: {
    type: String,
    required: false,
    default: '100%',
  },
  constLabel: {
    type: String,
    required: false,
    default: '',
  },
  emptyLabel: {
    type: String,
    required: false,
    default: '',
  },
  disabledOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'changeTdStyle'])

const { t } = useI18n()

const isOpen = ref(false)
const searchQuery = ref('')
const selectedValues = ref([...(props.modelValue || [])].map(String))
const menuId = `msdd-${Math.random().toString(36).slice(2, 9)}`

const dropdown = ref(null)
const button = ref(null)
const menu = ref(null)
const menuStyle = ref({})

function sameIds(a, b) {
  const left = [...(a || [])].map(String).sort()
  const right = [...(b || [])].map(String).sort()
  return left.length === right.length && left.every((v, i) => v === right[i])
}

function isSelected(value) {
  return selectedValues.value.includes(String(value))
}

function isOptionDisabled(value) {
  return props.disabled || props.disabledOptions.map(String).includes(String(value))
}

/**
 * Переключает опцию на mousedown по всей строке.
 * Нативный click по чекбоксу ломается из‑за scale(:active) — hit-box уезжает до mouseup.
 *
 * @param {string|number} value значение опции.
 */
function toggleOption(value) {
  if (isOptionDisabled(value)) {
    return
  }
  const id = String(value)
  const next = [...selectedValues.value]
  const idx = next.indexOf(id)
  if (idx === -1) {
    next.push(id)
  } else {
    next.splice(idx, 1)
  }
  selectedValues.value = next
  emit('update:modelValue', next)
}

const selectedLabels = computed(() => {
  if (props.constLabel !== '') {
    return props.constLabel
  }
  if (selectedValues.value.length === 0) {
    return props.emptyLabel || t('pickOptions')
  }
  return props.options
    .filter(option => selectedValues.value.includes(String(option.value)))
    .map(option => option.label)
    .join('[..]')
})

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option => option.label.toLowerCase().includes(query))
})

function updateMenuPosition() {
  if (!isOpen.value || !button.value) return
  const rect = button.value.getBoundingClientRect()
  const { dropUp, maxHeight, left, width, top, bottom } = viewportMenuPlacement(rect, {
    maxHeight: 320,
  })
  const gap = 6
  const { width: viewW, height: viewH } = cssViewportSize()
  menuStyle.value = {
    position: 'fixed',
    top: dropUp ? 'auto' : `${bottom + gap}px`,
    bottom: dropUp ? `${viewH - top + gap}px` : 'auto',
    left: `${left}px`,
    width: 'max-content',
    minWidth: `${Math.ceil(width)}px`,
    maxWidth: `${Math.max(48, viewW - 16)}px`,
    maxHeight: `${maxHeight}px`,
    overflowX: 'hidden',
    overflowY: 'auto',
    zIndex: '2000',
    boxSizing: 'border-box',
  }
  nextTick(() => {
    const el = menu.value
    if (!el || !isOpen.value) return
    const box = cssRectFromVisual(el.getBoundingClientRect())
    let nextLeft = left
    if (box.right > viewW - 8) {
      nextLeft = Math.max(8, viewW - 8 - box.width)
    }
    const clipPath = clipPathBelowModuleHeader(el.getBoundingClientRect().top)
    if (menuStyle.value.left !== `${nextLeft}px` || menuStyle.value.clipPath !== clipPath) {
      menuStyle.value = { ...menuStyle.value, left: `${nextLeft}px`, clipPath }
    }
  })
}

const toggleDropdown = () => {
  if (props.disabled) {
    return
  }
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    searchQuery.value = ''
  } else {
    nextTick(() => updateMenuPosition())
  }
  emit('changeTdStyle', isOpen.value)
}

const handleClickOutside = event => {
  const target = event.target
  if (dropdown.value?.contains(target) || menu.value?.contains(target)) {
    return
  }
  if (isOpen.value) {
    isOpen.value = false
    searchQuery.value = ''
    emit('changeTdStyle', false)
  }
}

watch(
  () => props.modelValue,
  newValues => {
    const next = [...(newValues || [])].map(String)
    if (!sameIds(next, selectedValues.value)) {
      selectedValues.value = next
    }
  }
)

watch(isOpen, open => {
  if (open) {
    nextTick(() => updateMenuPosition())
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
  window.addEventListener(PAGE_ZOOM_EVENT, updateMenuPosition)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
  window.removeEventListener(PAGE_ZOOM_EVENT, updateMenuPosition)
})
</script>

<i18n>
{
  "ru": {
    "pickOptions": "Выберите опции"
  },
  "en": {
    "pickOptions": "Select options"
  }
}
</i18n>

<style scoped>
.multi-select-dropdown {
  font-size: inherit;
}

.dropdown-menu-scrollable {
  max-height: min(320px, 50vh);
  overflow-y: auto;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item.disabled {
  cursor: not-allowed;
}
</style>
